import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import { libraryError } from '@/lib/libraryAccess';
import { Download, AlertTriangle } from 'lucide-react';
import { useLibraryBranding } from '@/lib/libraryBranding';

// Import dialog: first attempt without confirm_repeat; a 409 LIBRARY_ALREADY_IMPORTED switches to explicit repeat confirmation.
export function LibraryImportDialog({ item, open, onOpenChange, onImported }) {
  const brand = useLibraryBranding();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [repeat, setRepeat] = useState(null); // { existing_copies, previously_imported_versions }

  const reset = () => { setError(null); setRepeat(null); setBusy(false); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const submit = async (confirmRepeat) => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post(`/forms/library/${item.id}/import`, confirmRepeat ? { confirm_repeat: true } : {});
      reset(); onImported(data.data);
    } catch (e) {
      const err = libraryError(e);
      if (err.code === 'LIBRARY_ALREADY_IMPORTED') setRepeat(err.details || {});
      else setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid="library-import-dialog">
        <DialogHeader>
          <DialogTitle>{repeat ? 'Already imported — import another copy?' : 'Import into your Form Templates'}</DialogTitle>
          <DialogDescription data-testid="library-import-description">
            {item.name} · {brand.packLabel(item.pack_version)}. A new DRAFT template is created for your organisation. It is an independent copy: editing it never changes the library master, and it is not published or assigned until you do so.
          </DialogDescription>
        </DialogHeader>
        {repeat && (
          <div className="rounded-md border p-3 text-sm space-y-1" data-testid="library-repeat-warning">
            <p className="font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" />Your organisation already imported this master.</p>
            <ul className="text-xs text-muted-foreground list-disc pl-5" data-testid="library-existing-copies">
              {(repeat.existing_copies || []).map(c => <li key={c.template_id}>{c.form_code} · {c.status} · from v{c.source_pack_version}</li>)}
            </ul>
            <p className="text-xs text-muted-foreground">Existing copies are never overwritten. Confirming creates another independent copy.</p>
          </div>
        )}
        {error && (
          <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="library-import-error">
            <p className="font-medium">{error.message}</p>
            {error.code && <p className="text-xs font-mono mt-1" data-testid="library-import-error-code">{error.code}</p>}
            {error.details?.missing_capabilities?.length > 0 && (
              <p className="text-xs mt-1">Missing: {error.details.missing_capabilities.map(c => <Badge key={c} variant="outline" className="ml-1 text-xs">{c}</Badge>)}</p>
            )}
            {error.details?.active_pack_version && <p className="text-xs mt-1">Current library version: v{error.details.active_pack_version}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={busy} data-testid="library-import-cancel">Cancel</Button>
          {repeat ? (
            <Button onClick={() => submit(true)} disabled={busy} data-testid="library-import-repeat-confirm"><Download className="w-4 h-4 mr-2" />{busy ? 'Importing…' : 'Import another copy'}</Button>
          ) : (
            <Button onClick={() => submit(false)} disabled={busy || !!error} data-testid="library-import-confirm"><Download className="w-4 h-4 mr-2" />{busy ? 'Importing…' : 'Import as draft'}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
